import { Fragment, useMemo, useState } from "react";
import { PersonTasks } from "../../components/Tasks";
import { ProvinceField } from "../../components/Province";
import { Pager, SortTh, usePaged, useSorted } from "../../components/Pager";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { navigate, setQuery, useRoute } from "../../lib/router";
import { BookMeetingButton } from "../../components/Appointments";
import { useRefData } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Select, Seg, Sheet, daysAgo, fmtDate, fmtShort, useToast } from "../../components/ui";
import { useLog } from "../../components/QuickLog";
import { openPerson } from "../../components/PersonDrawer";
import { MembershipActions, MembershipDate } from "../../components/Membership";
import { IconCheck, IconSearch } from "../../components/icons";

export type Member = {
  member_id: string; person_id: string; full_name: string; phone: string | null; email: string | null; city: string | null; province: string | null;
  agent_code: string | null; level: string | null; status: string; licensed: boolean; role: string; recruit_date: string | null;
  recruiter_id: string | null; recruiter: string | null; direct_team: number; total_downline: number;
  deadline_warn_date: string | null; deadline_9m: string | null; deadline_12m: string | null; deadline_soon: boolean;
  membership_original?: string | null; membership_extended?: boolean; membership_expired?: boolean;
  last_contact: string | null; days_since_contact: number | null; contact_health: string | null; spark: string | null;
  open_commitments: number; overdue_commitments: number; overdue_milestones?: number; next_follow_up: string | null; progress: number | null; on_tracker: boolean;
  birth_month: number | null; birth_day: number | null;
};

export const useMembers = () => useQuery("team:members", () => db.select<Member>("team_overview", { select: "*", order: "full_name" }));

export function Health({ h }: { h: string | null }) {
  if (!h) return null;
  return <span className={clsx("health", h.split(" ")[0])}><i className="dot-i" />{h}</span>;
}

// ───────────────────────────── Overview ─────────────────────────────
export function TeamOverview() {
  const members = useMembers();
  const all = members.data ?? [];
  const active = all.filter((m) => m.status === "Active");
  const year = dayjs().year();
  const stat = (v: number | string, l: string, to?: string) => (
    <div className="stat"><div className="v">{v}</div>{to ? <button className="l" onClick={() => navigate(to)}>{l}</button> : <div className="l">{l}</div>}</div>
  );
  const commitments = useQuery("team:commitments", () =>
    db.select("activities", { area: eq("team"), commitment: "not.is.null", commitment_status: "in.(Pending,Rescheduled)", select: "id,person_id,commitment,commitment_due,topic,people(full_name)", order: "commitment_due.asc.nullslast", limit: 30 }));
  const overdue = (commitments.data ?? []).filter((c: any) => c.commitment_due && c.commitment_due < dayjs().format("YYYY-MM-DD"));
  const longest = active.filter((m) => m.contact_health === "Red" || m.contact_health === "Not contacted")
    .sort((a, b) => (b.days_since_contact ?? 9999) - (a.days_since_contact ?? 9999));
  const bdays = active.filter((m) => m.birth_month).map((m) => {
    const d = dayjs().month(m.birth_month! - 1).date(m.birth_day!);
    const next = d.isBefore(dayjs(), "day") ? d.add(1, "year") : d;
    return { m, next, days: next.diff(dayjs().startOf("day"), "day") };
  }).filter((x) => x.days <= 14).sort((a, b) => a.days - b.days);

  return (
    <>
      <PageHead title="Team overview" sub="Who needs a check-in, who is close to their licensing deadline, and how this year's recruits are moving." />
      <ErrorBox error={members.error} />
      {members.loading && !members.data ? <Loading /> : (
        <>
          <div className="stat-row">
            {stat(active.length, "Active team members", "/team/roster?status=Active")}
            {stat(active.filter((m) => m.licensed).length, "Licensed", "/team/roster?status=Active&licensed=yes")}
            {stat(active.filter((m) => m.deadline_soon).length, "Membership expiring in 90 days", "/team/roster?filter=deadline")}
            {stat(active.filter((m) => m.contact_health === "Not contacted").length, "Never checked in", "/team/roster?filter=nocontact")}
            {stat(overdue.length, "Overdue commitments")}
            {stat(active.reduce((n, m) => n + (m.overdue_milestones ?? 0), 0), "Tracker steps overdue", "/reports?view=team")}
            {stat(all.filter((m) => m.recruit_date && dayjs(m.recruit_date).year() === year).length, `Recruited in ${year}`, "/team/tracker")}
          </div>
          <div className="today-cols">
            <div style={{ display: "grid", gap: 14 }}>
              <Panel title="Follow up now" hint="Their own deadline has passed">
                {overdue.length ? (
                  <ul className="task-list">
                    {overdue.map((c: any) => (
                      <li key={c.id} className="task">
                        <button className="name" onClick={() => openPerson(c.person_id)}>{c.people?.full_name}</button>
                        <div className="task-actions"><CommitmentButtons id={c.id} /></div>
                        <div className="why">{c.commitment} <span className="pill alert">due {fmtShort(c.commitment_due)}</span></div>
                      </li>
                    ))}
                  </ul>
                ) : <Empty title="No overdue commitments" />}
              </Panel>
              <Panel title="Longest without a check-in" hint={`${longest.length} active members are red or never contacted`}>
                <MemberRows rows={longest} initial={{ key: "days", dir: "desc" }} />
              </Panel>
            </div>
            <aside style={{ display: "grid", gap: 14 }}>
              <Panel title="Membership expiring" hint="Active, not yet licensed · extended dates count">
                <ul className="task-list">
                  {active.filter((m) => m.deadline_soon).sort((a, b) => (a.deadline_9m ?? "").localeCompare(b.deadline_9m ?? "")).map((m) => (
                    <li key={m.member_id} className="task">
                      <button className="name" onClick={() => navigate(`/team/members/${m.member_id}`)}>{m.full_name}</button>
                      <div className="why"><MembershipDate m={m} long /> · {daysLeft(m.deadline_9m)}</div>
                    </li>
                  ))}
                </ul>
              </Panel>
              {bdays.length > 0 && (
                <Panel title="Team birthdays" hint="Next two weeks">
                  <ul className="task-list">
                    {bdays.map(({ m, next, days }) => (
                      <li key={m.member_id} className="task"><button className="name" onClick={() => openPerson(m.person_id)}>{m.full_name}</button>
                        <div className="why">{days === 0 ? "Today" : next.format("dddd, MMM D")}</div></li>
                    ))}
                  </ul>
                </Panel>
              )}
            </aside>
          </div>
        </>
      )}
    </>
  );
}

function CommitmentButtons({ id }: { id: string }) {
  const toast = useToast();
  const set = async (s: string) => {
    await db.update("activities", { id: eq(id) }, { commitment_status: s });
    invalidate("team"); invalidate("cc"); toast(s === "Done" ? "Marked kept" : "Marked broken");
  };
  return <><button className="btn small" onClick={() => set("Done")}>Kept</button><button className="btn small ghost" onClick={() => set("Missed")}>Broken</button></>;
}

/** Whole days from today to a date, the same way the database counts them. */
export const daysLeft = (d?: string | null) => {
  if (!d) return "";
  const n = dayjs(d).startOf("day").diff(dayjs().startOf("day"), "day");
  return n === 0 ? "today" : n === 1 ? "1 day left" : n < 0 ? `${-n} days ago` : `${n} days left`;
};

function MemberRows({ rows, initial }: { rows: Member[]; initial?: { key: string; dir: "asc" | "desc" } }) {
  const healthRank: Record<string, number> = { "Not contacted": 0, Red: 1, Amber: 2, Green: 3 };
  const { sorted, sort, toggle } = useSorted(rows, {
    name: (m) => m.full_name, recruiter: (m) => m.recruiter, licensed: (m) => (m.licensed ? 1 : 0), joined: (m) => m.recruit_date,
    contact: (m) => m.last_contact, health: (m) => (m.contact_health ? healthRank[m.contact_health] ?? 9 : null), progress: (m) => m.progress ?? 0,
    days: (m) => (m.status !== "Active" ? null : m.days_since_contact ?? 100000),
  }, (initial ?? { key: "name", dir: "asc" }) as any);
  const paged = usePaged(sorted, "roster", 25);
  return (
    <>
      <div className="table-wrap">
        <table className="data">
          <thead><tr>
            <SortTh k="name" sort={sort} onSort={toggle}>Name</SortTh>
            <SortTh k="recruiter" sort={sort} onSort={toggle}>Recruiter</SortTh>
            <SortTh k="joined" sort={sort} onSort={toggle} firstDir="desc">Joined</SortTh>
            <SortTh k="licensed" sort={sort} onSort={toggle} firstDir="desc">Licensed</SortTh>
            <SortTh k="days" sort={sort} onSort={toggle} firstDir="desc">Last check-in</SortTh>
            <SortTh k="health" sort={sort} onSort={toggle}>Health</SortTh>
            <th>Spark</th>
            <SortTh k="progress" sort={sort} onSort={toggle} firstDir="desc">Progress</SortTh>
          </tr></thead>
          <tbody>
            {paged.view.map((m) => (
              <tr key={m.member_id} onClick={() => navigate(`/team/members/${m.member_id}`)}>
                <td><strong>{m.full_name}</strong><div className="small muted">{m.agent_code}</div></td>
                <td>{m.recruiter}</td>
                <td className="small nowrap">{fmtShort(m.recruit_date)}</td>
                <td>{m.licensed ? "Yes" : "No"}</td>
                <td className="small">{daysAgo(m.last_contact)}</td>
                <td><Health h={m.contact_health} /></td>
                <td className="small">{m.spark ?? "—"}</td>
                <td className="small" style={{ minWidth: 90 }}>{m.on_tracker ? `${Math.round((m.progress ?? 0) * 100)}%` : <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager p={paged} noun="people" />
    </>
  );
}

// ───────────────────────────── Roster ─────────────────────────────
export function TeamRoster() {
  const { query } = useRoute();
  const { can } = useSession();
  const members = useMembers();
  const status = query.get("status") ?? "Active";
  const lic = query.get("licensed") ?? "";
  const filter = query.get("filter") ?? "";
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const rows = useMemo(() => (members.data ?? []).filter((m) =>
    (!status || m.status === status) && (!lic || (lic === "yes") === m.licensed) &&
    (filter !== "deadline" || m.deadline_soon) && (filter !== "extended" || !!m.membership_extended) && (filter !== "expired" || !!m.membership_expired) &&
    (filter !== "nocontact" || m.contact_health === "Not contacted" || m.contact_health === "Red") &&
    (!q || `${m.full_name} ${m.agent_code} ${m.recruiter}`.toLowerCase().includes(q.toLowerCase()))), [members.data, status, lic, filter, q]);

  return (
    <>
      <PageHead title="Roster" sub="Everyone on the team, with their deadlines and how recently they were checked in with."
        actions={can("team", "edit") && <button className="btn primary" onClick={() => setAdding(true)}>Add team member</button>} />
      <div className="toolbar">
        <div className="search grow"><IconSearch size={16} /><input className="input" placeholder="Search name, agent code or recruiter" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <Seg value={status} onChange={(v) => setQuery({ status: v })} options={[{ value: "Active", label: "Active" }, { value: "Terminated", label: "Terminated" }, { value: "", label: "Everyone" }]} />
        <Select value={lic} onChange={(v) => setQuery({ licensed: v || null })} options={[{ value: "yes", label: "Licensed" }, { value: "no", label: "Not licensed" }]} placeholder="Licensed or not" style={{ width: 170 }} />
        <Select value={filter} onChange={(v) => setQuery({ filter: v || null })} options={[{ value: "deadline", label: "Membership expiring soon" }, { value: "extended", label: "Membership extended" }, { value: "expired", label: "Membership expired" }, { value: "nocontact", label: "Red or never contacted" }]} placeholder="All" style={{ width: 210 }} />
      </div>
      <ErrorBox error={members.error} />
      <Panel hint={`${rows.length} people`} title=" ">
        {members.loading && !members.data ? <Loading rows={8} /> : rows.length === 0 ? <Empty title="No one matches these filters" /> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Name</th><th>Code</th><th>Level</th><th>Recruiter</th><th>Recruited</th><th>Licensed</th><th>Membership expires</th><th>Last check-in</th><th>Health</th><th className="num">Team</th></tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.member_id} onClick={() => navigate(`/team/members/${m.member_id}`)}>
                    <td><strong>{m.full_name}</strong><div className="small muted">{[m.city, m.province].filter(Boolean).join(", ")}</div></td>
                    <td className="small">{m.agent_code}</td>
                    <td className="small">{m.level}</td>
                    <td>{m.recruiter}</td>
                    <td className="nowrap small">{fmtShort(m.recruit_date)}</td>
                    <td>{m.licensed ? <span className="pill warm">Yes</span> : <span className="pill">No</span>}</td>
                    <td className="small"><MembershipDate m={m} /></td>
                    <td className="nowrap small">{daysAgo(m.last_contact)}</td>
                    <td><Health h={m.contact_health} /></td>
                    <td className="num small">{m.total_downline || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {adding && <AddMember members={members.data ?? []} onClose={() => setAdding(false)} />}
    </>
  );
}

function AddMember({ members, onClose }: { members: Member[]; onClose: () => void }) {
  const ref = useRefData();
  const toast = useToast();
  const [f, setF] = useState<Record<string, string>>({ status: "Active", recruit_date: dayjs().format("YYYY-MM-DD") });
  const [error, setError] = useState<Error | null>(null);
  const set = (k: string) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  async function save() {
    try {
      if (!f.first_name) throw new Error("Add a first name.");
      const [p] = await db.insert<{ id: string }>("people", { first_name: f.first_name, last_name: f.last_name ?? "", phone: f.phone || null, email: f.email || null, city: f.city || null, province: f.province || null, country: f.country || null });
      const rec = members.find((m) => m.member_id === f.recruiter_id);
      const [m] = await db.insert<{ id: string }>("team_members", {
        person_id: p.id, agent_code: f.agent_code || null, recruiter_id: f.recruiter_id || null, recruiter_name: rec?.full_name ?? null,
        recruiter_code: rec?.agent_code ?? null, level: f.level || null, status: "Active", licensed: false, recruit_date: f.recruit_date || null,
      });
      invalidate("team"); toast(`Added ${f.first_name} to the team`); onClose(); navigate(`/team/members/${m.id}`);
    } catch (e) { setError(e as Error); }
  }
  return (
    <Sheet title="Add team member" onClose={onClose} footer={<button className="btn primary" onClick={save}>Add team member</button>}>
      <ErrorBox error={error} />
      <div className="form-grid">
        <Field label="First name"><input className="input" value={f.first_name ?? ""} onChange={(e) => set("first_name")(e.target.value)} /></Field>
        <Field label="Last name"><input className="input" value={f.last_name ?? ""} onChange={(e) => set("last_name")(e.target.value)} /></Field>
        <Field label="Agent code"><input className="input" value={f.agent_code ?? ""} onChange={(e) => set("agent_code")(e.target.value)} /></Field>
        <Field label="Recruited by"><Select value={f.recruiter_id} onChange={set("recruiter_id")} options={members.filter((m) => m.status === "Active").map((m) => ({ value: m.member_id, label: `${m.full_name}${m.agent_code ? ` (${m.agent_code})` : ""}` }))} /></Field>
        <Field label="Recruit date"><input type="date" className="input" value={f.recruit_date ?? ""} onChange={(e) => set("recruit_date")(e.target.value)} /></Field>
        <Field label="Level"><Select value={f.level} onChange={set("level")} options={ref.data?.list("AgentLevels") ?? []} /></Field>
        <Field label="Phone"><input className="input" value={f.phone ?? ""} onChange={(e) => set("phone")(e.target.value)} /></Field>
        <Field label="Email"><input className="input" type="email" value={f.email ?? ""} onChange={(e) => set("email")(e.target.value)} /></Field>
        <Field label="City"><input className="input" value={f.city ?? ""} onChange={(e) => set("city")(e.target.value)} /></Field>
        <ProvinceField province={f.province} country={f.country} onChange={(province, country) => setF((x: any) => ({ ...x, province, country }))} />
      </div>
    </Sheet>
  );
}

// ───────────────────────────── Member profile ─────────────────────────────
export function MemberProfile({ id }: { id: string }) {
  const { can } = useSession();
  const log = useLog();
  const toast = useToast();
  const ref = useRefData();
  const m = useQuery(`team:member:${id}`, () => db.one<Member>("team_overview", { member_id: eq(id), select: "*" }));
  const ms = useQuery(`team:milestones:${id}`, () => db.select("member_milestone_status", { member_id: eq(id), select: "*", order: "sort" }));
  const recruits = useQuery(`team:recruits:${id}`, () => db.select<Member>("team_overview", { recruiter_id: eq(id), select: "member_id,full_name,status,licensed,contact_health", order: "status,full_name" }));
  const acts = useQuery(m.data ? `team:acts:${m.data.person_id}` : null, () =>
    db.select("activities", { person_id: eq(m.data!.person_id), workspace: eq("team"), select: "*", order: "occurred_on.desc,created_at.desc", limit: 100 }));
  const keep = useQuery(m.data ? `team:keep:${m.data.person_id}` : null, () =>
    db.one<{ kept: number; broken: number; late: number; keep_rate: number | null }>("commitment_keep", { person_id: eq(m.data!.person_id), select: "*" }));
  const [editing, setEditing] = useState(false);
  const editable = can("team", "edit");

  async function toggle(row: any) {
    if (!editable) return;
    if (row.is_count) {
      const v = window.prompt(`${row.label}: how many?`, String(row.count_value ?? 0));
      if (v === null) return;
      await db.upsert("member_milestones", { member_id: id, milestone_key: row.milestone_key, count_value: Number(v) || 0, done: false }, "member_id,milestone_key");
    } else {
      await db.upsert("member_milestones", { member_id: id, milestone_key: row.milestone_key, done: !row.done, done_on: !row.done ? dayjs().format("YYYY-MM-DD") : null }, "member_id,milestone_key");
      if (!row.done) toast(`${row.label} done`);
    }
    invalidate(`team:milestones:${id}`); invalidate("team:members");
  }

  if (m.loading && !m.data) return <Loading rows={8} />;
  if (!m.data) return <Empty title="Team member not found">They may have been removed, or you don't have access.</Empty>;
  const x = m.data;
  const k = keep.data;
  return (
    <>
      <PageHead title={x.full_name}
        sub={<>{x.agent_code ?? "No agent code"} · {x.status} · {x.licensed ? "Licensed" : "Not licensed"}{x.recruiter && <> · Recruited by {x.recruiter}</>}</>}
        actions={<>
          {can("activities", "edit") && <button className="btn primary" onClick={() => log({ person_id: x.person_id, person_name: x.full_name, area: "team" })}>Log check-in</button>}
          <button className="btn" onClick={() => openPerson(x.person_id)}>Contact & family</button>
          <BookMeetingButton person={{ id: x.person_id, full_name: x.full_name }} />
          {editable && <button className="btn ghost" onClick={() => setEditing(true)}>Edit</button>}
        </>} />
      <div className="stat-row">
        <div className="stat"><div className="v">{x.progress != null ? `${Math.round(x.progress * 100)}%` : "—"}</div><div className="l">Onboarding progress</div></div>
        <div className="stat"><div className="v"><Health h={x.contact_health} /></div><div className="l">Last check-in {daysAgo(x.last_contact)}</div></div>
        <div className="stat"><div className="v">{x.spark ?? "—"}</div><div className="l">Spark (latest)</div></div>
        <div className="stat"><div className="v">{k?.keep_rate != null ? `${Math.round(k.keep_rate * 100)}%` : "—"}</div>
          <div className="l">Promises kept{k ? ` · ${k.kept} kept, ${k.broken} broken${k.late ? `, ${k.late} late` : ""}` : ""}</div></div>
        {!x.licensed && x.deadline_9m && <div className="stat"><div className="v" style={{ fontSize: 18 }}><MembershipDate m={x} long /></div>
          <div className="l">Membership expires{x.membership_extended ? ` · 9-month mark was ${fmtShort(x.membership_original)}` : x.deadline_soon ? " · coming up" : ""}</div></div>}
        <div className="stat"><div className="v">{x.direct_team}</div><div className="l">Direct recruits · {x.total_downline} in downline</div></div>
      </div>
      <MembershipActions m={x} />
      <div className="today-cols">
        <div style={{ display: "grid", gap: 14 }}>
          <Panel title="Recruit tracker" hint={x.recruit_date ? `Due dates count from the recruit date, ${fmtDate(x.recruit_date)}` : "Add a recruit date to see due dates"}>
            <div className="panel-body">
              <div className="milestones">
                {(ms.data ?? []).map((r: any) => (
                  <button key={r.milestone_key} className="ms" data-done={r.done} data-overdue={r.overdue} onClick={() => toggle(r)} disabled={!editable}>
                    <span className="box">{r.done && <IconCheck size={13} />}{r.is_count && <span className="small">{r.count_value ?? 0}</span>}</span>
                    <span>
                      <div className="t">{r.label}</div>
                      <div className="d">{r.done ? (r.done_on ? `Done ${fmtShort(r.done_on)}` : "Done") : r.due_on ? `${r.overdue ? "Was due" : "Due"} ${fmtShort(r.due_on)}` : r.due_label ?? ""}</div>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </Panel>
          <Panel title="Check-ins and moves">
            <div className="panel-body">
              {acts.data?.length ? (
                <ul className="timeline">
                  {acts.data.map((a: any) => (
                    <li key={a.id}>
                      <time>{fmtShort(a.occurred_on)}</time>
                      <div>
                        <div className="what">{a.activity_type}{a.topic && <span className="muted"> · {a.topic}</span>}{a.contacted_by && <span className="muted"> · by {a.contacted_by}</span>}</div>
                        {a.what_happened && <div className="sub">{a.what_happened}</div>}
                        {a.commitment && <div className="sub">Committed to: {a.commitment}{a.commitment_due && ` by ${fmtShort(a.commitment_due)}`} <span className={clsx("pill", a.commitment_status === "Done" && "warm", a.commitment_status === "Missed" && "alert")}>{a.commitment_status}</span></div>}
                        {a.spark && <div className="small muted">Spark: {a.spark}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : <Empty title="No check-ins yet">Log the first one to start tracking contact health.</Empty>}
            </div>
          </Panel>
        </div>
        <aside style={{ display: "grid", gap: 14 }}>
          <Panel title="Details">
            <div className="panel-body">
              <dl className="kv">
                <dt>Phone</dt><dd>{x.phone ?? "—"}</dd>
                <dt>Email</dt><dd>{x.email ?? "—"}</dd>
                <dt>Lives in</dt><dd>{[x.city, x.province].filter(Boolean).join(", ") || "—"}</dd>
                <dt>Level</dt><dd>{x.level ?? "—"}</dd>
                <dt>Recruited</dt><dd>{fmtDate(x.recruit_date) || "—"}</dd>
                <dt>12-month mark</dt><dd>{fmtDate(x.deadline_12m) || "—"}</dd>
              </dl>
            </div>
          </Panel>
          <Panel><div className="panel-body"><PersonTasks person={{ id: x.person_id, full_name: x.full_name }} /></div></Panel>
          <Panel title="Their recruits">
            {recruits.data?.length ? (
              <ul className="task-list">
                {recruits.data.map((r) => (
                  <li key={r.member_id} className="task">
                    <button className="name" onClick={() => navigate(`/team/members/${r.member_id}`)}>{r.full_name}</button>
                    <div className="why">{r.status}{r.licensed ? " · Licensed" : ""} {r.status === "Active" && <Health h={r.contact_health} />}</div>
                  </li>
                ))}
              </ul>
            ) : <Empty title="No recruits yet" />}
          </Panel>
        </aside>
      </div>
      {editing && <EditMember m={x} levels={ref.data?.list("AgentLevels") ?? []} onClose={() => setEditing(false)} />}
    </>
  );
}

function EditMember({ m, levels, onClose }: { m: Member; levels: string[]; onClose: () => void }) {
  const toast = useToast();
  const members = useMembers();
  const [f, setF] = useState<any>({ agent_code: m.agent_code, level: m.level, status: m.status, licensed: m.licensed, recruit_date: m.recruit_date, recruiter_id: m.recruiter_id });
  const [error, setError] = useState<Error | null>(null);
  async function save() {
    try {
      const rec = members.data?.find((x) => x.member_id === f.recruiter_id);
      await db.update("team_members", { id: eq(m.member_id) }, { ...f, recruiter_name: rec?.full_name ?? null, recruiter_code: rec?.agent_code ?? null, recruiter_id: f.recruiter_id || null });
      invalidate("team"); toast("Saved"); onClose();
    } catch (e) { setError(e as Error); }
  }
  return (
    <Sheet title={`Edit ${m.full_name}`} onClose={onClose} footer={<button className="btn primary" onClick={save}>Save changes</button>}>
      <ErrorBox error={error} />
      <div className="form-grid">
        <Field label="Agent code"><input className="input" value={f.agent_code ?? ""} onChange={(e) => setF({ ...f, agent_code: e.target.value || null })} /></Field>
        <Field label="Level"><Select value={f.level} onChange={(v) => setF({ ...f, level: v || null })} options={levels} /></Field>
        <Field label="Status"><Select value={f.status} onChange={(v) => setF({ ...f, status: v || "Active" })} options={["Active", "Terminated"]} /></Field>
        <Field label="Recruit date"><input type="date" className="input" value={f.recruit_date ?? ""} onChange={(e) => setF({ ...f, recruit_date: e.target.value || null })} /></Field>
        <Field label="Recruited by"><Select value={f.recruiter_id} onChange={(v) => setF({ ...f, recruiter_id: v || null })} options={(members.data ?? []).filter((x) => x.member_id !== m.member_id).map((x) => ({ value: x.member_id, label: x.full_name }))} /></Field>
        <label className="check" style={{ alignSelf: "end" }}><input type="checkbox" checked={!!f.licensed} onChange={(e) => setF({ ...f, licensed: e.target.checked })} /> Licensed</label>
      </div>
    </Sheet>
  );
}

// ───────────────────────────── Recruit tracker grid ─────────────────────────────
export function RecruitTracker() {
  const ref = useRefData();
  const members = useMembers();
  const { can } = useSession();
  const toast = useToast();
  const editable = can("team", "edit");
  const { query } = useRoute();
  const year = query.get("year") ?? String(dayjs().year());
  const group = (query.get("group") ?? "month") as "month" | "quarter" | "none";
  const statusF = query.get("status") ?? "Active";
  const [q, setQ] = useState("");
  const ms = useQuery("team:allmilestones", () => db.select("member_milestones", { select: "member_id,milestone_key,done,count_value", order: "member_id,milestone_key" }));
  const defs = ref.data?.milestones ?? [];
  const msMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const x of (ms.data ?? []) as any[]) m.set(`${x.member_id}|${x.milestone_key}`, x);
    return m;
  }, [ms.data]);
  const get = (mid: string, key: string) => msMap.get(`${mid}|${key}`);
  const years = [...new Set((members.data ?? []).filter((m) => m.recruit_date).map((m) => String(dayjs(m.recruit_date).year())))].sort().reverse();

  const filtered = useMemo(() => (members.data ?? []).filter((m) =>
    m.recruit_date && (year === "all" || dayjs(m.recruit_date).year() === Number(year)) &&
    (statusF === "all" || m.status === statusF) &&
    (!q || `${m.full_name} ${m.agent_code ?? ""} ${m.recruiter ?? ""}`.toLowerCase().includes(q.toLowerCase()))), [members.data, year, statusF, q]);
  const { sorted, sort, toggle } = useSorted(filtered, {
    date: (m) => m.recruit_date, name: (m) => m.full_name, progress: (m) => m.progress ?? 0, recruiter: (m) => m.recruiter,
  }, { key: "date", dir: "asc" });
  const grouped = group !== "none" && sort.key === "date";
  const paged = usePaged(sorted, "recruit-tracker", 25);

  const keyOf = (m: Member) => group === "quarter" ? `${dayjs(m.recruit_date).year()} · Q${Math.ceil((dayjs(m.recruit_date).month() + 1) / 3)}` : dayjs(m.recruit_date).format("MMMM YYYY");
  const groups = useMemo(() => {
    if (!grouped) return [{ key: "", list: paged.view }];
    const out: { key: string; list: Member[] }[] = [];
    for (const m of paged.view) { const k = keyOf(m); const g = out[out.length - 1]; if (g && g.key === k) g.list.push(m); else out.push({ key: k, list: [m] }); }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paged.view, grouped, group]);
  // roll-ups use the whole group, not just the visible page
  const groupAll = (k: string) => sorted.filter((m) => keyOf(m) === k);

  async function tick(m: Member, d: any) {
    if (!editable || d.is_count) return navigate(`/team/members/${m.member_id}`);
    const v = get(m.member_id, d.key);
    await db.upsert("member_milestones", { member_id: m.member_id, milestone_key: d.key, done: !v?.done, done_on: !v?.done ? dayjs().format("YYYY-MM-DD") : null }, "member_id,milestone_key");
    invalidate("team:allmilestones"); invalidate("team:members"); invalidate("dash:team");
    if (!v?.done) toast(`${d.label} ✓ for ${m.full_name}`);
  }
  const pct = (n: number | null | undefined) => `${Math.round((n ?? 0) * 100)}%`;
  const bar = (n: number | null | undefined) => (
    <div className="pbar"><div className="progress"><i style={{ width: pct(n), background: (n ?? 0) >= 0.75 ? "var(--warm)" : (n ?? 0) >= 0.4 ? "var(--sky-ink)" : "var(--gold)" }} /></div><span>{pct(n)}</span></div>
  );

  return (
    <>
      <PageHead title="Recruit tracker" sub="Every recruit's first steps. Click a dot to tick a step off; click a name to open their profile." />
      <div className="filters">
        <span className="lbl">Year</span>
        <select className="select" value={year} onChange={(e) => setQuery({ year: e.target.value })}>
          <option value="all">All years</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="lbl">Status</span>
        <select className="select" value={statusF} onChange={(e) => setQuery({ status: e.target.value })}>
          <option value="Active">Active</option><option value="Terminated">Terminated</option><option value="all">Everyone</option>
        </select>
        <span className="lbl">Group by</span>
        <Seg value={group} onChange={(v) => setQuery({ group: v })} options={[{ value: "month", label: "Month" }, { value: "quarter", label: "Quarter" }, { value: "none", label: "None" }]} />
        <div className="search" style={{ flex: "1 1 200px", maxWidth: 280 }}><IconSearch size={16} /><input className="input" placeholder="Name, code or recruiter" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      </div>
      <Panel>
        {members.loading && !members.data ? <Loading rows={8} /> : sorted.length === 0 ? <Empty title="No recruits match" /> : (<>
          <div className="table-wrap tracker">
            <table className="data">
              <thead><tr>
                <SortTh k="name" sort={sort} onSort={toggle}>Recruit</SortTh>
                <SortTh k="date" sort={sort} onSort={toggle}>Joined</SortTh>
                {defs.map((d) => <th key={d.key} className="ms-th" title={d.due_label ?? ""}><span>{d.label}</span>{d.due_label && <small>{d.due_label}</small>}</th>)}
                <SortTh k="progress" sort={sort} onSort={toggle} firstDir="desc" style={{ minWidth: 130 }}>Progress</SortTh>
              </tr></thead>
              <tbody>
                {groups.map((g) => {
                  const all = grouped ? groupAll(g.key) : [];
                  const avg = all.length ? all.reduce((s, m) => s + (m.progress ?? 0), 0) / all.length : 0;
                  return (
                    <Fragment key={g.key || "flat"}>
                      {grouped && (
                        <tr className="grp">
                          <td colSpan={2}><b>{g.key}</b> <span className="muted small">· {all.length} joined</span></td>
                          {defs.map((d) => {
                            const n = d.is_count ? all.reduce((s, m) => s + Number(get(m.member_id, d.key)?.count_value ?? 0), 0) : all.filter((m) => get(m.member_id, d.key)?.done).length;
                            return <td key={d.key} className={clsx("ms-cell", n === 0 && "zero", !d.is_count && n >= all.length && n > 0 && "full")}>{n}</td>;
                          })}
                          <td>{bar(avg)}</td>
                        </tr>
                      )}
                      {g.list.map((m) => (
                        <tr key={m.member_id} className={clsx(m.status !== "Active" && "term")}>
                          <td className="nowrap"><button className="linkish" onClick={() => navigate(`/team/members/${m.member_id}`)}><strong>{m.full_name}</strong></button>
                            <div className="small muted">{m.agent_code} · {m.recruiter}{m.status !== "Active" ? " · Terminated" : ""}</div></td>
                          <td className="nowrap small">{fmtShort(m.recruit_date)}</td>
                          {defs.map((d) => {
                            const v: any = get(m.member_id, d.key);
                            return (
                              <td key={d.key} className="ms-cell">
                                {d.is_count ? <button className="ms-count" onClick={() => tick(m, d)}>{v?.count_value ?? 0}</button> :
                                  <button className={clsx("ms-dot", v?.done && "on")} disabled={!editable} onClick={() => tick(m, d)}
                                    aria-label={`${d.label}: ${v?.done ? "done" : "not done"}`} title={`${d.label}${d.due_label ? ` · due ${d.due_label}` : ""}`}>{v?.done ? "✓" : ""}</button>}
                              </td>
                            );
                          })}
                          <td>{bar(m.progress)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager p={paged} noun="recruits" />
        </>)}
      </Panel>
    </>
  );
}

// ───────────────────────────── Tree ─────────────────────────────
